/**
 * Single source of truth for brand, contact, legal, navigation and page
 * metadata.
 *
 * Placeholder values follow the registry conventions (`01632 960xxx`,
 * `@example.com`). Anything marked TO CONFIRM is recorded in
 * docs/registry.md under Placeholders and must be supplied before launch.
 */
import { IMAGES } from './images'

export const BRAND = {
  /**
   * The full trading name, as it appears on the roundel on the bar front
   * and printed on the merchandise: "LOW ON FUEL CAFÉ". Everything that
   * names the business reads from here.
   */
  name: 'Low On Fuel Café',
  /**
   * The name without "Café", for the handful of places where the word
   * would immediately repeat itself — "Low On Fuel Café café" on the
   * Visit page, or a page title already ending in a café noun.
   */
  shortName: 'Low On Fuel',
  /**
   * Sits under the wordmark. Was "Derbyshire café", which stacked a second
   * "café" directly beneath "Low On Fuel Café". Location is the more
   * useful thing to say there anyway.
   */
  shortDescriptor: 'Brackenfield · Derbyshire',
  domain: 'lowonfuel.com',
  tagline: 'Strong coffee. Cool bikes. Good company.',
  description:
    'A biker café and community hub on the edge of the Peak District.',
  /**
   * The client's final brand mark: the roundel, the same one painted on
   * the bar front and hung above the front door. Header and footer both
   * read from here, so there is one asset to change.
   */
  logoUrl:
    'https://cdn.magicpatterns.com/uploads/8iECnezEuYQK513YbgnwLp/MASTER_Final_Low_on_Fuel_Logo.webp',
} as const

/**
 * Confirmed by the client 2026-09-12: the full address is
 * "Low On Fuel Cafe, Matlock Road, Brackenfield, DE55 6DD".
 *
 * `county` is retained because the client's own single line omits it but
 * Derbyshire appears throughout the prose, and an address block that
 * disagrees with the copy beside it is worse than a slightly long one.
 */
export const CONTACT = {
  // Ofcom reserved drama range. Never a real dialling code.
  telephone: '01632 960960',
  telephoneHref: 'tel:+441632960960',
  /**
   * General enquiries. THE MAILBOX MUST EXIST BEFORE LAUNCH: the legal
   * documents name it as the route for data subject requests and
   * accessibility problems, both of which carry a response deadline.
   * Registry L03.
   */
  email: 'hello@lowonfuel.com',
  /**
   * The proprietor directly. Used ONLY on the company-details page, where
   * the law expects a route to the person behind the business. Everything
   * else — forms, footer, legal documents, group enquiries — goes to
   * `email`, per the client's instruction. Registry D40.
   */
  proprietorEmail: 'andy@lowonfuel.com',
  address: {
    line1: 'Matlock Road',
    townOrCity: 'Brackenfield',
    county: 'Derbyshire',
    postcode: 'DE55 6DD',
    country: 'United Kingdom',
  },
  /** Rendered under the address as orientation, not as a claim. */
  orientation: 'Gateway to the Peak District · Easy access from the A38 and A610',
} as const

export const ADDRESS_SINGLE_LINE = `${CONTACT.address.line1}, ${CONTACT.address.townOrCity}, ${CONTACT.address.county}, ${CONTACT.address.postcode}`

/** As the client gives it, for the maps link and structured data. */
export const ADDRESS_WITH_NAME = `Low On Fuel Cafe, ${CONTACT.address.line1}, ${CONTACT.address.townOrCity}, ${CONTACT.address.postcode}`

/**
 * The address EXACTLY as it appears on the café's Google Business
 * Profile, which uses the post town rather than the village.
 *
 * Used ONLY on the business-details page, at the client's instruction, so
 * the formal record matches the listing a reader might check it against.
 * Everywhere else says Brackenfield, which is where the café actually is
 * and what the branding says. The two are not in conflict: Brackenfield
 * is a village within the Alfreton post town. Registry D42.
 */
export const GBP_ADDRESS = 'Matlock Rd, Alfreton DE55 6DD'

/**
 * A SOLE TRADER, not a limited company. Confirmed by the client
 * 2026-09-12: Andrew Martin, trading as Low On Fuel Café, operating from
 * and resident at the café.
 *
 * There is deliberately NO company number or place of registration here.
 * A sole trader has neither, and the earlier shape of this record — built
 * for a limited company — was quietly inviting two invented fields. The
 * Companies Act disclosure rules do not apply; the Business Names
 * provisions of the Companies Act 2006 do, which require the proprietor's
 * name and a business address wherever a trading name is used. Both are
 * present. Registry D40.
 */
export const LEGAL_ENTITY = {
  structure: 'sole trader' as const,
  /** The person legally responsible. Required disclosure. */
  proprietorName: 'Andrew Martin',
  tradingName: 'Low On Fuel Café',
  /** Required disclosure. Also the café itself — the two are the same. */
  businessAddress: ADDRESS_WITH_NAME,
  /** For the governing-law clause in the terms. */
  jurisdiction: 'England and Wales',
} as const

/**
 * CONFIRMED 2026-09-12 from a screenshot of the café's Google Business
 * Profile: closed Mondays, 10:00–21:00 every other day. Grouped into two
 * rows because seven identical rows is a table nobody reads, and the one
 * fact that changes someone's plans — Monday — earns its own line.
 *
 * The deferral note below is still worth rendering: the Google listing is
 * where hours are actually kept current, and it is the only place a bank
 * holiday change will appear. Registry I10.
 */
export const OPENING_HOURS = [
  { days: 'Tuesday – Sunday', hours: '10am – 9pm' },
  { days: 'Monday', hours: 'Closed' },
] as const

/** Called out separately wherever a wasted journey is the cost of missing it. */
export const CLOSED_DAY_NOTE = 'We are closed on Mondays.'

export const OPENING_HOURS_NOTE =
  'Our Google listing is the one we keep up to date, so check there for bank holidays and any change to these times.'

/**
 * Response-time promise renders from the form register in
 * docs/registry.md (F01, F02) so the page and the record cannot drift.
 */
export const FORM_RESPONSE_TIME = 'within 2 working days'

/**
 * Six primary destinations plus one CTA — the chassis maximum. "About the
 * café" is deliberately not a seventh item: it is the opening section of
 * the home page and the body of the Visit page.
 */
export const NAV_ITEMS = [
  { to: '/', label: 'Home' },
  { to: '/whats-on', label: "What's on" },
  { to: '/food-and-drink', label: 'Food & drink' },
  { to: '/shop', label: 'Shop' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/visit', label: 'Visit' },
] as const

/**
 * The header action. A telephone link rather than a route, so it must
 * render as an anchor with `href`, never a react-router `Link`.
 *
 * "Call The Gaffa" says nothing about who or what number on its own, so
 * `ariaLabel` carries the number for anyone who cannot see the header
 * beside it. Registry D38.
 */
export const HEADER_CTA = {
  href: CONTACT.telephoneHref,
  label: 'Call The Gaffa',
  shortLabel: 'Call',
  ariaLabel: `Call The Gaffa on ${CONTACT.telephone}`,
} as const

export const FOOTER_NAV = [
  { to: '/whats-on', label: "What's on" },
  { to: '/food-and-drink', label: 'Food & drink' },
  { to: '/shop', label: 'Shop' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/visit', label: 'Visit us' },
] as const

/**
 * Only published destinations. The guided run and wrench night were
 * hidden at the client's request (D41), so linking to them here would
 * have produced three dead footer links on every page.
 */
export const FOOTER_MORE = [
  { to: '/whats-on/friday-bike-night', label: 'Friday Bike Night' },
  { to: '/food-and-drink', label: 'The Fuel Stop menu' },
  { to: '/shop', label: 'The shop' },
  { to: '/gallery', label: 'Pictures of the place' },
] as const

/**
 * Fixed order: Privacy · Cookies · Terms · Accessibility, then the
 * statutory company disclosure. All five are real routes now, derived from
 * `legalDocs` rather than typed out, so the footer and the document
 * navigation cannot disagree about what exists.
 *
 * Defined as a getter rather than a constant to avoid a circular import:
 * `data/legal.ts` reads BRAND and CONTACT from this file.
 */
export const LEGAL_LINK_ORDER = [
  'privacy',
  'cookies',
  'terms',
  'accessibility',
] as const

/**
 * The café's real profiles, supplied by the client 2026-09-12.
 *
 * Facebook comes first: it is the café's main channel and where bike
 * night is actually announced, so it leads rather than sitting in
 * alphabetical order.
 *
 * These leave the site, so every consumer must render them with
 * `target="_blank"` and `rel="noreferrer"`, and announce that fact to a
 * screen reader rather than leaving an icon that silently steals the tab.
 */
export const SOCIAL_LINKS = [
  {
    href: 'https://www.facebook.com/p/Low-On-Fuel-Cafe-61577956263676/',
    label: 'Facebook',
    platform: 'facebook' as const,
  },
  {
    href: 'https://www.instagram.com/lowonfuelcafe/',
    label: 'Instagram',
    platform: 'instagram' as const,
  },
] as const

export const SITE_URL = `https://${BRAND.domain}`

/**
 * Default share image when a page has nothing more specific. The bike
 * night crowd shot, because it is the one photograph that says what the
 * place is without a caption.
 */
export const DEFAULT_OG_IMAGE = IMAGES.bikeNightCrowd

/**
 * Per-page title and description. The registry's page table renders from
 * the same record, so the two cannot drift.
 */
export const PAGE_META = {
  home: {
    title: 'Low On Fuel | Motorcycle café in the heart of Derbyshire',
    description:
      'A gritstone biker café on the hill road at Brackenfield, with a deck looking over the Derbyshire valley. Strong coffee, proper baps, gelato, a bar, and a bike night every Friday.',
  },
  whatsOn: {
    title: "What's on | Low On Fuel",
    description:
      'Friday Bike Night, every Friday from 6pm, all year round. The car park fills with bikes and the place fills with the riders. No booking, no cover charge, just turn up.',
  },
  foodAndDrink: {
    title: 'Food & Drink at the Fuel Stop | Low On Fuel',
    description:
      'All our hot food comes from the Fuel Stop, our catering truck in the car park, open every day we are: breakfast baps all day, handmade pies and the smashed burger. Inside there is a coffee bar, a cabinet of cakes, the ice cream counter and a proper bar. Just walk up and order. Card only, mind.',
  },
  shop: {
    title: 'Low On Fuel Shop',
    description:
      'One-off reclaimed pieces, handmade bits from makers near us, and our own tees, caps and snoods. Reserve something by dropping us a line, then collect it from the café.',
  },
  gallery: {
    title: 'Gallery | Low On Fuel',
    description:
      'The door, the bar, half a dozen rooms of stone walls with veteran motorcycles up on them, the Fuel Stop hatch, the ice cream counter, and a Friday night with the road full of bikes.',
  },
  visit: {
    title: 'Visit us | Low On Fuel',
    description:
      'Matlock Road, Brackenfield, Derbyshire. Opening hours, parking, how to find us and how to get hold of us.',
  },
  notFound: {
    title: 'Page not found | Low On Fuel',
    description: 'That page is not here. Here is the way back.',
  },
} as const
